import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const PherpharArjForm = () => {
  const componentRef = useRef();
  const { language } = useLanguage();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: 'Pherphar_Arj.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      
      {/* Controls Container - Hidden when printing */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'प्रिंट करा' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'पीडीएफ डाउनलोड करा' : 'Download PDF'}
        </button>
      </div>

      {/* A4 Paper Container */}
      <div 
        ref={componentRef}
        className="max-w-[210mm] mx-auto bg-white p-12 shadow-lg text-black print:shadow-none print:max-w-none print:w-[210mm] print:h-[297mm] print:mx-0 print:p-12 relative"
        style={{ minHeight: '297mm' }}
      >
        
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold border-b-2 border-black inline-block pb-1">
            {language === 'marathi' ? 'फेरफार / नामांतर करण्यासाठी अर्ज' : 'Application for Mutation / Name Change'}
          </h1>
        </div>

        {/* Date Container */}
        <div className="flex justify-end mb-6 text-[15px]">
          <p>{language === 'marathi' ? 'दिनांक: &nbsp; &nbsp; &nbsp; / &nbsp; &nbsp; &nbsp; /२०' : 'Date: &nbsp; &nbsp; &nbsp; / &nbsp; &nbsp; &nbsp; /20'}</p>
        </div>

        {/* Sender Info Grid */}
        <div className="mb-6 text-[15px] leading-relaxed">
          <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
          <p className="font-bold">{language === 'marathi' ? 'सरपंच / ग्रामपंचायत अधिकारी,' : 'Sarpanch / Gram Panchayat Officer,'}</p>
          
          <div className="mt-4">
            <p className="flex items-end">
              <span className="shrink-0 mr-1">{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'}</span>
              <span className="border-b border-dashed border-black w-48"></span>
            </p>
            <p className="flex items-end mt-1">
              <span className="shrink-0 mr-1">{language === 'marathi' ? 'ता.' : 'Taluka:'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
              <span className="shrink-0 mx-1">{language === 'marathi' ? 'जि.' : 'Dist.:'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
            </p>
          </div>
        </div>

        {/* Subject */}
        <div className="pl-16 mb-8 text-[15px] leading-relaxed">
           <p className="font-bold">{language === 'marathi' ? 'विषय :- फेरफार / नामांतर करणे बाबत .' : 'Subject :- Regarding Mutation / Name Change.'}</p>
           <div className="mt-2 text-[15px]">
             <p className="flex items-end font-bold">
               <span className="shrink-0 mr-2">{language === 'marathi' ? 'अर्जदार :-' : 'Applicant :-'}</span>
               <span className="font-normal shrink-0 mr-1">{language === 'marathi' ? 'श्री. / सौ.' : 'Shri. / Smt.'}</span>
               <span className="border-b border-dashed border-black font-normal flex-grow"></span>
             </p>
             <p className="flex items-end mt-2 pl-16">
               <span className="shrink-0 mr-2">{language === 'marathi' ? 'रा.' : 'Residing at'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
               <span className="shrink-0 mx-2">{language === 'marathi' ? 'मो.नं.' : 'Mob.No.'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
             </p>
           </div>
        </div>

        {/* Body Text Part 1 */}
        <div className="mb-6 text-[15px] leading-relaxed">
          <p>{language === 'marathi' ? 'मा. महोदय,' : 'Respected Sir/Madam,'}</p>
          <p className="pl-10 mt-3 flex items-end flex-wrap leading-loose">
            <span className="mr-1">{language === 'marathi' ? 'वरील विषयान्वये सविनय अर्ज सादर करतो / करते की,ग्रा. पं. गावठाण हद्दीतील मौजे' : 'With reference to the above subject, I humbly submit the application that, within the Gram Panchayat village limits at Mouje'}</span>
            <span className="border-b border-dashed border-black grow shrink-0 min-w-[50px] inline-block" style={{ marginBottom: '5px' }}></span>
          </p>
          <p className="flex items-end flex-wrap mt-1 leading-loose">
             <span className="mr-1 mt-1">{language === 'marathi' ? 'येथील मि.क्र.' : 'here Property No.'}</span>
             <span className="border-b border-dotted border-black w-48 mt-1 inline-block" style={{ marginBottom: '5px'}}></span>
             <span className="mx-1 mt-1">{language === 'marathi' ? 'मुळ मालक' : 'Original Owner'}</span>
             <span className="border-b border-dashed border-black grow mt-1 inline-block" style={{ marginBottom: '5px'}}></span>
             <span className="ml-1 mt-1">{language === 'marathi' ? 'यांच्या कडून' : 'from them'}</span>
          </p>
          <p className="mt-1">{language === 'marathi' ? 'मी/आम्ही' : 'I/We'}</p>
          <div className="pl-2 mt-2 space-y-2">
            <p>{language === 'marathi' ? '१) रजिष्टर खरेदी खताने खरेदी केली आहे.' : '1) Purchased by registered sale deed.'}</p>
            <p>{language === 'marathi' ? '२) वारसाहक्कनुसार वाटणी पत्रा આધारे/ संमतीपत्रा આધारे वाटणी केली आहे.' : '2) Partitioned on the basis of partition deed / consent deed as per inheritance rights.'}</p>
            <p className="flex items-end">
               <span className="shrink-0 mr-2">{language === 'marathi' ? '३)' : '3)'}</span>
               <span className="border-b border-dashed border-black grow min-w-[50px]"></span>
            </p>
          </div>
        </div>

        {/* Body Text Part 2 */}
        <div className="mb-8 text-[15px] leading-relaxed">
           <p className="mb-2">{language === 'marathi' ? 'सदरील मिळकतीची चतु:सिमा खालील प्रमाणे आहे.' : 'The boundaries of the said property are as follows.'}</p>
           <div className="pl-4 space-y-2 pr-12">
              <p className="flex items-end">
                 <span className="shrink-0 w-24">{language === 'marathi' ? 'पूर्वेस :' : 'East :'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
              </p>
              <p className="flex items-end">
                 <span className="shrink-0 w-24">{language === 'marathi' ? 'पश्चिमेस :' : 'West :'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
              </p>
              <p className="flex items-end">
                 <span className="shrink-0 w-24">{language === 'marathi' ? 'दक्षिणेस :' : 'South :'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
              </p>
              <p className="flex items-end">
                 <span className="shrink-0 w-24">{language === 'marathi' ? 'उत्तरेस :-' : 'North :-'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
              </p>
           </div>
        </div>

        {/* Closing paragraph */}
        <div className="mb-12 text-[15px] leading-relaxed">
           <p className="pl-8 inline">{language === 'marathi' ? 'नियमानुसार सर्व थकबाकी व फिस भरण्यास मी तयार आहे.करिता सदरील फेरफार माझ्या नावे /आमच्या नावे' : 'I am ready to pay all arrears and fees as per rules. Therefore, the said mutation in my name / our name'}</p>
           <p className="mt-1">{language === 'marathi' ? 'करण्यात यावा. ही नम्र विनंती.' : 'should be done. This is a humble request.'}</p>
        </div>

        {/* Documents and Signature aligned parallel */}
        <div className="flex justify-between items-start text-[15px] mb-8 w-full pr-4">
           {/* Left side: Documents Section */}
           <div className="w-[55%]">
              <p className="font-bold underline underline-offset-4 mb-3">{language === 'marathi' ? 'सोबत :' : 'Enclosures :'}</p>
              <div className="space-y-2">
                 <p>{language === 'marathi' ? '१) रजिष्टर खरेदीखताची छायांकितप्रत' : '1) Photocopy of Registered Sale Deed'}</p>
                 <p>{language === 'marathi' ? '२) वाटणीपत्र/ संमतीपत्र' : '2) Partition Deed / Consent Deed'}</p>
                 <p>{language === 'marathi' ? '३) मृत्यू प्रमाणपत्र छायांकित प्रत' : '3) Photocopy of Death Certificate'}</p>
                 <p>{language === 'marathi' ? '४) वारस प्रमाणपत्र' : '4) Heirship Certificate'}</p>
                 <p className="flex items-end">
                    <span className="shrink-0 mr-1">{language === 'marathi' ? '५)' : '5)'}</span>
                    <span className="border-b border-dashed border-black w-56"></span>
                 </p>
              </div>
           </div>

           {/* Right side: Signatures */}
           <div className="w-[45%] flex flex-col justify-end min-h-[160px] pb-5">
              <div className="space-y-2">
                 <p className="flex items-end">
                   <span className="shrink-0 mr-2">{language === 'marathi' ? 'अर्जदाराचे नाव' : 'Applicant\'s Name'}</span>
                   <span className="border-b border-dashed border-black grow min-w-[50px]"></span>
                 </p>
                 <p className="flex items-end">
                   <span className="shrink-0 mr-2">{language === 'marathi' ? 'स्वाक्षरी / अंगठयाचा ठसा.' : 'Signature / Thumb Impression.'}</span>
                   <span className="border-b border-dashed border-black grow min-w-[50px]"></span>
                 </p>
              </div>
           </div>
        </div>

        {/* Website Link (Absolute at the bottom left) */}
        <div className="absolute bottom-12 left-12 text-blue-700 text-sm font-semibold underline">
          www.majhigrampanchayat.com
        </div>

      </div>
    </div>
  );
};

export default PherpharArjForm;
